import React from 'react';

interface Props {
  label: string;
  value: string | number;
  unit?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendLabel?: string;
  icon?: React.ReactNode;
  color?: string;
  onClick?: () => void;
  /** 7-day daily values for sparkline */
  sparkline?: number[];
}

const TREND_STYLES = {
  up:      { arrow: '↑', cls: 'text-arete-green' },
  down:    { arrow: '↓', cls: 'text-arete-red' },
  neutral: { arrow: '→', cls: 'text-arete-muted' },
};

function MiniSparkline({ data, color }: { data: number[]; color: string }) {
  const valid = data.filter(v => isFinite(v) && !isNaN(v) && v > 0);
  if (valid.length < 2) return null;
  const w = 48, h = 16, pad = 1;
  const min = Math.min(...valid) - (Math.max(...valid) - Math.min(...valid)) * 0.15;
  const max = Math.max(...valid) + (Math.max(...valid) - Math.min(...valid)) * 0.15;
  const rng = max - min || 1;
  const pts = valid.map((v, i) => `${pad + (i / (valid.length - 1)) * (w - pad * 2)},${h - pad - ((v - min) / rng) * (h - pad * 2)}`);
  return (
    <svg width={w} height={h} style={{ opacity: 0.8 }}>
      <polygon points={`${pts.join(' ')} ${w - pad},${h - pad} ${pad},${h - pad}`} fill={color} opacity={0.12} />
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function MetricCard({ label, value, unit, trend, trendLabel, icon, color = '#1E6FBF', onClick, sparkline }: Props) {
  return (
    <div
      className={`card flex flex-col gap-3 transition-shadow duration-150 ${onClick ? 'cursor-pointer hover:shadow-card-hover' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <span className="section-label">{label}</span>
        {icon && (
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}18` }}>
            <span style={{ color }}>{icon}</span>
          </div>
        )}
      </div>

      <div className="flex items-end justify-between">
        <div className="flex items-end gap-1.5">
          <span className="text-3xl font-bold text-arete-navy dark:text-gray-100 leading-none">{value ?? '–'}</span>
          {unit && <span className="text-sm text-arete-muted mb-0.5">{unit}</span>}
        </div>
        {sparkline && sparkline.length >= 2 && (
          <MiniSparkline data={sparkline} color={color} />
        )}
      </div>

      {trend && trendLabel && (
        <div className={`flex items-center gap-1 text-xs font-medium ${TREND_STYLES[trend].cls}`}>
          <span>{TREND_STYLES[trend].arrow}</span>
          <span>{trendLabel}</span>
        </div>
      )}
    </div>
  );
}
